import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import GIButton from '@components/common/button/GIButton';
import Dropdown from '@lib/inputs/Dropdown';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import type { IMobileColumn, ISortByEntry, SetSortBy } from './types';
import { ConditionalRendering } from '@components/common/conditional-rendering';

interface IMobileSortHeaderProps {
    columns: IMobileColumn[];
    sortBy: ISortByEntry[];
    setSortBy: SetSortBy;
    idPrefix: string;
}

const SORT_PLACEHOLDER_VALUE = 'default';

const getColumnSortId = (column: IMobileColumn): string => column.id ?? column.accessor ?? '';

const MobileSortHeader = ({ columns, sortBy, setSortBy, idPrefix }: IMobileSortHeaderProps) => {
    const intl = useIntl();

    const sortableColumns = useMemo(
        () =>
            columns.filter(
                (column: IMobileColumn) => column.isSortEnabled !== false && typeof column.Header === 'string',
            ),
        [columns],
    );

    const options = useMemo(
        () => [
            { value: SORT_PLACEHOLDER_VALUE, text: intl.formatMessage({ id: 'common.sortBy' }) },
            ...sortableColumns.map((column: IMobileColumn) => ({
                value: getColumnSortId(column),
                text: intl.formatMessage({ id: (column.mobileCardLabel ?? column.Header) as string }),
            })),
        ],
        [sortableColumns, intl],
    );

    if (!sortableColumns.length) {
        return null;
    }

    const currentSort = sortBy?.[0];
    const currentSortId = currentSort?.id ?? SORT_PLACEHOLDER_VALUE;
    const isDescending = Boolean(currentSort?.desc);

    const handleFieldChange = (nextId: string) => {
        if (!nextId || nextId === SORT_PLACEHOLDER_VALUE) {
            return;
        }
        setSortBy([{ id: nextId, desc: isDescending }]);
    };

    const handleToggleDirection = () => {
        const activeId = currentSortId || getColumnSortId(sortableColumns[0]);
        setSortBy([{ id: activeId, desc: !isDescending }]);
    };

    const directionLabel = intl.formatMessage({
        id: isDescending ? 'common.table.header.sortDescending' : 'common.table.header.sortAscending',
    });

    return (
        <div className="gi-mobile-sort" data-testid={`${idPrefix}-sort-header`}>
            <label className="gi-mobile-sort__label" id="sort-by-label" htmlFor={`${idPrefix}-sort-select`}>
                <FormattedMessage id="common.sortBy" defaultMessage="Sort by" />
            </label>
            <Dropdown
                inputData={{
                    id: `${idPrefix}-sort-select`,
                    name: `${idPrefix}-sort-select`,
                    type: 'dropdown',
                    value: currentSortId,
                    isInputRequired: false,
                    inputClass: ['gi-mobile-sort__select'],
                    options,
                }}
                label={{}}
                dropdownContainerClassName="gi-mobile-sort__dropdown"
                inputActions={{
                    handleFieldChange,
                    onFieldBlur: () => {},
                }}
            />
            <ConditionalRendering isTrue={currentSortId !== SORT_PLACEHOLDER_VALUE}>
                <GIButton
                    id={`${idPrefix}-sort-direction`}
                    type="button"
                    variant="outline"
                    icon={<FontAwesomeIcon icon={isDescending ? faArrowDown : faArrowUp} />}
                    iconPlacement="end"
                    className="gi-mobile-sort__button"
                    ariaLabel={directionLabel}
                    dataTestId={`${idPrefix}-sort-direction`}
                    onClickHandler={handleToggleDirection}
                />
            </ConditionalRendering>
        </div>
    );
};

export default MobileSortHeader;
